/**
 * «Мои заявки» — /requests (UC-G.06, canon design-guest §17).
 *
 * Список заявок гостя: карточки-строки (название · категория · статус-бейдж
 * · дата), новые сверху. Тап → /requests/{id} (карточка с трекингом + чат).
 * Состояния: loading (skeleton-строки), empty («Заявок пока нет» + CTA в
 * каталог), error (toast retry).
 *
 * Гость видит ТОЛЬКО свои заявки — ownership на бэке (GET /v1/me/requests).
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Card, Skeleton } from '@hsc/ui';
import type { RequestView } from '@hsc/types';
import { Toast } from '../../shared/ui/Toast.js';
import { Toaster, useToasts } from '../requests-kanban/Toast.js';
import { CreateRequestForm } from '../requests-kanban/CreateRequestForm.js';
import { useMyRequests } from './useRequests.js';
import { statusMeta } from './status.js';

/** Дата для строки списка: «13 июня, 14:30». */
function formatWhen(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('ru-RU', {
    day: 'numeric',
    month: 'long',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Карточка-строка одной заявки в списке. */
function RequestRow({ rq, onOpen }: { rq: RequestView; onOpen: () => void }): JSX.Element {
  const meta = statusMeta(rq.state);
  return (
    <li>
      <button
        type="button"
        className="rq-row"
        data-testid={`request-row-${rq.id}`}
        onClick={onOpen}
      >
        <span className="rq-row__main">
          <span className="rq-row__title">{rq.title}</span>
          <span className="rq-row__meta">
            {rq.category}
            {rq.created_at !== '' ? ` · ${formatWhen(rq.created_at)}` : ''}
          </span>
        </span>
        <span className={`rq-badge rq-badge--${meta.tone}`}>{meta.label}</span>
        <span className="rq-row__chev" aria-hidden="true">
          ›
        </span>
      </button>
    </li>
  );
}

export interface MyRequestsPageProps {
  /**
   * Персонал (бар/кухня/уборщица) может завести заявку прямо со своего экрана
   * («+ Заявка», POST /v1/admin/requests). Гость кнопки не видит — он создаёт
   * обращения через каталог услуг. По умолчанию выключено (гостевой режим).
   */
  canCreate?: boolean;
}

export function MyRequestsPage({ canCreate = false }: MyRequestsPageProps = {}): JSX.Element {
  const navigate = useNavigate();
  const { data, isLoading, isError, refetch } = useMyRequests();
  const toasts = useToasts();
  const [creating, setCreating] = useState(false);

  // Кнопка «+ Заявка» в шапке — только для персонала. Открывает форму создания
  // (переиспользуем форму канбана: номер · категория · описание · приоритет).
  const newRequestButton = canCreate ? (
    <button
      type="button"
      className="rqk-new-btn"
      data-testid="my-requests-new"
      onClick={() => setCreating(true)}
    >
      + Заявка
    </button>
  ) : null;

  // Форма создания открыта — показываем её вместо списка (как на канбане).
  if (canCreate && creating) {
    return (
      <>
        <Toaster toasts={toasts.toasts} onDismiss={toasts.dismiss} />
        <CreateRequestForm
          onClose={() => setCreating(false)}
          onCreated={(title) => {
            toasts.success(`Заявка «${title}» создана`);
            void refetch();
          }}
        />
      </>
    );
  }

  if (isLoading) {
    return (
      <section aria-busy="true" data-testid="my-requests-skeleton">
        <div className="rqk-head">
        <h1 className="hsc-page__title rqk-head__titles">Мои заявки</h1>
        {newRequestButton}
      </div>
        <ul className="rq-list">
          {[0, 1, 2, 3].map((i) => (
            <li key={i}>
              <Skeleton height={64} radius={14} />
            </li>
          ))}
        </ul>
      </section>
    );
  }

  if (isError || !data) {
    return (
      <section data-testid="my-requests-error">
        <div className="rqk-head">
        <h1 className="hsc-page__title rqk-head__titles">Мои заявки</h1>
        {newRequestButton}
      </div>
        <Toast
          tone="error"
          message="Не удалось загрузить заявки"
          actionLabel="Повторить"
          onAction={() => void refetch()}
        />
        <Card>
          <p role="alert">Попробуйте обновить список — мы уже разбираемся.</p>
        </Card>
      </section>
    );
  }

  if (data.items.length === 0) {
    return (
      <section data-testid="my-requests-empty">
      <Toaster toasts={toasts.toasts} onDismiss={toasts.dismiss} />
        <div className="rqk-head">
        <h1 className="hsc-page__title rqk-head__titles">Мои заявки</h1>
        {newRequestButton}
      </div>
        {/* Персонал заводит заявки сам («+ Заявка»), а не через каталог услуг —
            поэтому ему даём другой пустой экран и CTA на создание. */}
        {canCreate ? (
          <Card>
            <h2 className="hsc-card__title">Заявок пока нет</h2>
            <p>
              Здесь появятся заявки, которые Вы создали или взяли в работу. Нужно
              что-то по смене — заведите заявку.
            </p>
            <Button onClick={() => setCreating(true)}>+ Создать заявку</Button>
          </Card>
        ) : (
          <Card>
            <h2 className="hsc-card__title">Заявок пока нет</h2>
            <p>
              Здесь появятся Ваши обращения к отелю. Нужна услуга — выберите её в каталоге, и мы
              всё устроим.
            </p>
            <Button onClick={() => navigate('/services')}>К услугам отеля</Button>
          </Card>
        )}
      </section>
    );
  }

  return (
    <section data-testid="my-requests-page">
      <Toaster toasts={toasts.toasts} onDismiss={toasts.dismiss} />
      <div className="rqk-head">
        <h1 className="hsc-page__title rqk-head__titles">Мои заявки</h1>
        {newRequestButton}
      </div>
      <p className="ta-muted">Всего: {data.count}</p>
      <ul className="rq-list">
        {data.items.map((rq) => (
          <RequestRow
            key={rq.id}
            rq={rq}
            onOpen={() => navigate(`/requests/${encodeURIComponent(rq.id)}`)}
          />
        ))}
      </ul>
    </section>
  );
}
